import React from 'react';
import { track } from '@leagueplatform/analytics';
import {
  BodyOne,
  Box,
  HeadingTwo,
  PrimaryButton,
  SecondaryButton,
} from '@leagueplatform/genesis-commons';
import {
  LinkLegacy as Link,
  useParamsLegacy as useParams,
} from '@leagueplatform/routing';
import { useIntl } from '@leagueplatform/locales';
import { ModalContentWithImage } from '#components/modal-content-with-image/modal-content-with-image.component.tsx';
import { useGetUserProfilePhotoUrl } from '#hooks/use-get-get-user-profile-photo-url.hook.ts';
import { useGetChallengeByIdData } from '#pages/challenge-landing-page/hooks/use-get-challenge-by-id-data.hook.ts';
import { CHALLENGE_CAMPAIGN_TYPE } from '#constants/analytics.constants.tsx';

import { BUTTON_CONTAINER_MAX_WIDTH } from '#constants/challenges.constants.tsx';
import { JOURNEY_ROUTES } from '@leagueplatform/health-journey-common';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { CHALLENGES_ASSET_KEYS } from '#types/challenges.types.ts';

interface SignUpModalProps {
  onClose: () => void;
  userCanJoinTeam: boolean;
}

export const SignUpSuccess = ({
  onClose,
  userCanJoinTeam,
}: SignUpModalProps) => {
  const { formatMessage, $tifelse } = useIntl();
  const { challengeId } = useParams<{ challengeId: string }>();
  const userPhotoUrl = useGetUserProfilePhotoUrl();
  const { data: challengeData } = useGetChallengeByIdData();
  const highFive = handleStaticAsset(
    CHALLENGES_ASSET_KEYS.CHALLENGES_HIGH_FIVE,
  ) as string;

  return (
    <ModalContentWithImage
      image={{
        src: userPhotoUrl ?? highFive,
        alt: '',
      }}
      isSuccessScreen
    >
      <Box textAlign={{ _: 'center', tablet: 'start' }}>
        <HeadingTwo as="h3">
          {formatMessage({
            id: 'YOURE_GOOD_TO_GO',
          })}
        </HeadingTwo>
        <BodyOne
          paddingTop={{ _: 'one', tablet: 'oneAndHalf' }}
          paddingBottom={{ _: 'one', tablet: 'oneAndHalf' }}
        >
          {$tifelse(
            userCanJoinTeam,
            'CHALLENGE_LETS_START_TEAMS_ENABLED',
            'CHECK_CHALLENGE_DETAILS',
          )}
        </BodyOne>
        <PrimaryButton
          onClick={() => {
            track(
              'challenges.button_clicked.challenge_sign_up_success_screen.navigate_to_challenge_details',
              {
                health_campaign_id: challengeId ?? '',
                health_campaign_name: challengeData?.data.title ?? '',
                health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
                challenge_status: challengeData?.data.status ?? '',
              },
            );
            onClose();
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(userCanJoinTeam && {
            as: Link,
            to: JOURNEY_ROUTES.getChallengeJoinTeam(challengeId),
          })}
          alignSelf="flex-start"
          width="100%"
          maxWidth={BUTTON_CONTAINER_MAX_WIDTH}
          marginTop="one"
          textDecoration="none"
        >
          {$tifelse(
            userCanJoinTeam,
            'JOIN_OR_CREATE_TEAM',
            'VIEW_CHALLENGE_DETAILS',
          )}
        </PrimaryButton>
        {/* change to genesis core styles */}
        <SecondaryButton
          onClick={() => {
            track(
              'challenges.button_clicked.challenge_sign_up_success_screen.navigate_to_journey',
              {
                health_campaign_id: challengeId ?? '',
                health_campaign_name: challengeData?.data.title ?? '',
                health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
                challenge_status: challengeData?.data.status ?? '',
              },
            );
            onClose();
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(!userCanJoinTeam && {
            as: Link,
            to: JOURNEY_ROUTES.journeyHome(),
            textDecoration: 'none',
          })}
          marginTop="oneAndHalf"
          width="100%"
          maxWidth={BUTTON_CONTAINER_MAX_WIDTH}
        >
          {$tifelse(userCanJoinTeam, 'NOT_NOW', 'GO_TO_JOURNEY')}
        </SecondaryButton>
      </Box>
    </ModalContentWithImage>
  );
};
